import { i18n } from '@lingui/core'
import { msg, Trans } from '@lingui/macro'
import { TableCell } from '@material-ui/core'
import { Add } from '@mui/icons-material'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import {
  Button,
  Grid,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableContainer,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material'
import { FormTopLabel } from 'app/views/common-components/forms/FormTopLabel'
import { TextFieldWithFormik } from 'app/views/common-components/with-formik/TextFieldWithFormik'
import { CurrencyFormat, CurrencyFormated } from 'app/views/common/Formats'
import { FieldArray, useFormikContext } from 'formik'
import { useState } from 'react'
import { getMainConnected } from '../../../Form'
import { endEditingField } from '../../../multiuser/grpcMultiuserEdit'
import './FormOtherGrants.css'

export const formOtherGrantsDefaultValue = (obj, info, item) => {
  const { connectedField } = getMainConnected(item)
  const field = connectedField?.name
  if (!field || !obj || !obj[field]) {
    return []
  }
  console.log('obj[field]: ', obj[field])

  return obj[field].split(';;;').map((i) => {
    const grant = i.split(';')
    return { name: grant[0], value: grant[1], state: grant[2] }
  })
}

export const grantStatusOptions = [
  {
    label: <Trans>Approved</Trans>,
    labelPDF: i18n._('Approved'),
    value: 'en_ApprovedUS'
  },
  {
    label: <Trans>Pending</Trans>,
    labelPDF: i18n._('Pending'),
    value: 'Pending'
  },
  {
    label: <Trans>Not submitted yet</Trans>,
    labelPDF: i18n._('Not submitted yet'),
    value: 'Not submitted yet'
  },
  {
    label: <Trans>Declined</Trans>,
    labelPDF: i18n._('Declined'),
    value: 'Declined'
  }
]

export const formOtherGrantsValueToText = (v) => {
  const toRet = (
    <div>
      <Grid container style={{ width: '100%' }}>
        <Grid item xs={5} style={{ padding: 4 }}>
          <Typography className='form-print-subtitle'>
            <Trans>Name</Trans>
          </Typography>
        </Grid>
        <Grid item xs style={{ padding: 4 }}>
          <Typography className='form-print-subtitle'>
            <Trans>Value</Trans>
          </Typography>
        </Grid>
        <Grid item xs style={{ padding: 4 }}>
          <Typography className='form-print-subtitle'>
            <Trans>Grants: State</Trans>
          </Typography>
        </Grid>
      </Grid>
      {v.map((grant, index) => {
        return (
          <>
            <div style={{ height: 8 }} />
            <Grid container key={index} style={{ width: '100%' }}>
              <Grid item xs={5} style={{ padding: 4 }}>
                <Typography>{grant.name}</Typography>
              </Grid>
              <Grid item xs style={{ padding: 4 }}>
                <Typography>
                  <CurrencyFormated value={grant.value} />
                </Typography>
              </Grid>
              <Grid item xs style={{ padding: 4 }}>
                <Typography>
                  <Trans id={grant.state} />
                </Typography>
              </Grid>
            </Grid>
          </>
        )
      })}
    </div>
  )
  return {
    en: toRet,
    fr: toRet
  }
}

export const formOtherGrantsExtractSaveKey = ({
  saveMap,
  item,
  sfObject,
  value,
  connectedProps,
  subObjectsMap,
  connectedObjectId
}) => {
  const { connectedField, connectedObject } = connectedProps
  const fieldName = connectedField?.name
  if (fieldName && connectedObject) {
    saveMap[connectedObject][fieldName] = value
      .map((i) => `${i.name};${i.value};${i.state}`)
      .join(';;;')
  }
}

/**
 * Form element which allows providing list of other grants that are related to the project.
 * @category Form
 * @subcategory Form elements
 * @component
 * @returns {JSX.Element}
 * @param  {SalesforceObjectConnection}  connectedObject Connected Salesforce object.
 * @param  {SalesforceFieldConnection}  connectedField Connected Salesforce field object the value of input will be save to. Accepts 'textarea' and 'string' type fields.
 */
export const FormOtherGrants = ({
  id,
  value,
  useMultiuser,
  muBag,
  disabled,
  title,
  tooltip,
  typeProps,
  langVersion,
  ...props
}) => {
  const { connectedTo = [], required } = typeProps
  const { values } = useFormikContext()
  const grants = value ?? []
  const [showTable, setShowTable] = useState(grants.length > 0)

  const isElementLocked = Object.keys(values?.muInfo).find((key) => {
    if (key.includes(id)) return true
    return false
  })

  if (connectedTo.length === 0) {
    return (
      <div style={{ color: 'red' }}>
        <b>
          <Trans>Grants field was not connected in form editor!</Trans>
        </b>
      </div>
    )
  }

  return (
    <FieldArray
      name={id}
      render={(arrayHelpers) => (
        <Grid
          container
          direction='column'
          spacing={1}
          style={{ marginBottom: 5 }}
          justifyContent='space-between'
        >
          <Grid container wrap='nowrap' alignItems='center' item xs={12} style={{ marginTop: 25 }}>
            <FormTopLabel required={required}>
              {title}
            </FormTopLabel>

            {tooltip && (
              <div>
                <Tooltip title={tooltip}>
                  <IconButton aria-label='delete' size='small'>
                    <HelpOutlineIcon fontSize='inherit' />
                  </IconButton>
                </Tooltip>
              </div>
            )}
          </Grid>

          {showTable && grants.length > 0 && (
            <Grid
              item
              sx={{
                display: 'flex',
                border: '1px solid #17374F',
                backgroundColor: '#fff',
                boxShadow: disabled ? 'none' : '0 4px 4px 0 rgba(0, 0, 0, 0.25)',
                marginLeft: '8px'
              }}
            >
              <Grid container direction='column'>
                <TableContainer
                  component='div'
                  sx={{
                    padding: '16px 0',
                    marginLeft: '-8px',
                    width: 'calc(100% + 8px)'
                  }}
                >
                  <Table
                    size='small'
                    aria-label='a dense table'
                    sx={{
                      whiteSpace: 'pre',
                      '& .MuiTableCell-root': { borderBottom: 'none' }
                    }}
                  >
                    <colgroup>
                      <col width='30%' />
                      <col width='30%' />
                      <col width='30%' />
                      <col width='10%' />
                    </colgroup>
                    <TableBody>
                      {grants.map((item, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            verticalAlign: 'top',
                            borderBottom: grants.length > 1 && index !== grants.length - 1
                              ? '1px solid rgba(0,0,0,0.25)'
                              : 'none'
                          }}
                        >
                          <TableCell sx={{ paddingLeft: '40px', paddingBottom: '24px' }}>
                            <FormTopLabel size='medium'><Trans>Grant name</Trans></FormTopLabel>
                            <TextFieldWithFormik
                              useMultiuser={useMultiuser}
                              displayFieldHistoryIcon
                              muBag={muBag}
                              name={`${id}[${index}].name`}
                              disabled={disabled}
                              sx={{ '& .MuiInputBase-root.Mui-disabled': { backgroundColor: '#0000000D' } }}
                            />
                          </TableCell>

                          <TableCell>
                            <FormTopLabel size='medium'><Trans>Grant value</Trans></FormTopLabel>
                            <TextFieldWithFormik
                              useMultiuser={useMultiuser}
                              displayFieldHistoryIcon
                              elementType='textInputNumeric'
                              InputProps={{ inputComponent: CurrencyFormat }}
                              placeholder={langVersion !== 'fr' ? '$0' : '0 $'}
                              muBag={muBag}
                              name={`${id}[${index}].value`}
                              type='number'
                              currency
                              disabled={disabled}
                              color='primary'
                              sx={{ '& .MuiInputBase-root.Mui-disabled': { backgroundColor: '#0000000D' } }}
                            />
                          </TableCell>

                          <TableCell sx={{ paddingBottom: '24px' }}>
                            <FormTopLabel size='medium'><Trans>Grant status</Trans></FormTopLabel>
                            <TextFieldWithFormik
                              select
                              useMultiuser={useMultiuser}
                              displayFieldHistoryIcon
                              muBag={muBag}
                              name={`${id}[${index}].state`}
                              disabled={disabled}
                              InputProps={{
                                sx: {
                                  height: '54px',
                                  '& .MuiSelect-select': {
                                    display: 'flex',
                                    alignItems: 'center',
                                    height: '100%',
                                    paddingTop: 0,
                                    paddingBottom: 0
                                  },
                                  '& .MuiSelect-select.Mui-disabled': { backgroundColor: '#0000000D' }
                                }
                              }}
                            >
                              {grantStatusOptions.map((item) => (
                                <MenuItem value={item.value} key={item.key}>
                                  {item.label}
                                </MenuItem>
                              ))}
                            </TextFieldWithFormik>
                          </TableCell>

                          <TableCell sx={{ paddingRight: '40px', paddingBottom: '24px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center' }}>
                            <FormTopLabel size='medium'><Trans>Delete</Trans></FormTopLabel>
                            <Tooltip title={<Trans>Delete the grant</Trans>}>
                              <IconButton
                                variant='filled'
                                onClick={() => {
                                  arrayHelpers.remove(index)
                                  if (grants.length === 1) setShowTable(false)
                                  const newValue = [...value]
                                  newValue.splice(index, 1)
                                  if (useMultiuser) {
                                    endEditingField({
                                      ...muBag,
                                      fieldId: id,
                                      fieldValue: newValue
                                    })
                                  }
                                }}
                                disabled={disabled || isElementLocked}
                                size='large'
                                sx={{ color: '#0000008A', '&.Mui-disabled': { color: '#0000004D' } }}
                              >
                                <DeleteOutlinedIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          )}

          <Grid item sx={{ marginTop: showTable ? '8px' : '0px' }}>
            <Button
              variant='contained'
              color='primary'
              type='button'
              disabled={disabled}
              onClick={() => {
                setShowTable(true)
                arrayHelpers.push({ name: '', value: 0, state: '' })
                if (useMultiuser) {
                  endEditingField({
                    ...muBag,
                    fieldId: id,
                    fieldValue: [
                      ...value,
                      {
                        name: '',
                        value: 0,
                        state: ''
                      }
                    ]
                  })
                }
              }}
            >
              <Add />
              <span><Trans>Add additional grants</Trans></span>
            </Button>
          </Grid>
        </Grid>
      )}
    />
  )
}

const otherGrantTranslations = {
  'Not submitted yet': msg`Not submitted yet`,
  Declined: msg`Declined`,
  Pending: msg`Pending`,
  Approved: msg`Approved`
}

export const formOtherGrantsValueToExportText = (value, _) => {
  if (!value) {
    return ''
  }

  return value.map((item, index) => {
    const stateTranslation =
      otherGrantTranslations[item.state] ||
      ((item.state === null || item.state === 'undefined') ? '' : item.state)

    return `${index + 1}. ${item?.name ? item.name : ''}: $${item?.value ? item.value : ''}, ${i18n._(stateTranslation)}`
  }).join('\n\n')
}
